import { useState, useEffect, useRef, useCallback } from 'react'
import { User, UserRole } from '@/types'
import { getApiUrl } from '@/lib/apiUrl'

interface MessageWithUser {
  id: string
  application_id?: string
  sender_id: string
  receiver_id: string
  content: string
  messageType?: 'text' | 'image' | 'file' | 'system'
  attachments?: Array<{
    filename?: string
    originalName?: string
    url?: string
    mimeType?: string
    size?: number
    uploadedAt?: string
  }>
  isRead?: boolean
  created_at: string
  updated_at: string
  sender: User
  receiver: User
}

interface UseOptimizedChatProps {
  applicationId: string
  currentUserId: string
}

// Backend message response types
interface BackendUser {
  _id?: string
  id?: string
  email?: string
  role?: string
  isVerified?: boolean
  is_verified?: boolean
  createdAt?: string
  created_at?: string
  firstName?: string
  lastName?: string
  avatar?: string
}

interface BackendMessage {
  _id?: string
  id?: string
  application?: string | { _id?: string; id?: string }
  applicationId?: string
  sender?: BackendUser | string
  sender_id?: string
  receiver?: BackendUser | string
  receiver_id?: string
  message?: string
  content?: string
  messageType?: 'text' | 'image' | 'file' | 'system'
  message_type?: 'text' | 'image' | 'file' | 'system'
  attachments?: Array<{
    filename?: string
    originalName?: string
    url?: string
    mimeType?: string
    size?: number
    uploadedAt?: string
  }>
  isRead?: boolean
  is_read?: boolean
  sentAt?: string
  sent_at?: string
  createdAt?: string
  created_at?: string
  updatedAt?: string
  updated_at?: string
}

type NormalizableId = string | { _id?: string; id?: string } | null | undefined

// Message cache to prevent redundant queries
const messageCache = new Map<string, MessageWithUser[]>()
const CACHE_DURATION = 30 * 1000 // 30 seconds for fresher data

// Polling interval for real-time updates
// Increased to 10 seconds to avoid rate limiting (100 requests per 15 minutes)
const POLLING_INTERVAL = 10000 // 10 seconds for near real-time chat

export function useOptimizedChat({ applicationId, currentUserId }: UseOptimizedChatProps) {
  const [messages, setMessages] = useState<MessageWithUser[]>([])
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const lastLoadTime = useRef<number>(0)
  const rateLimitBackoff = useRef<number>(0) // Track exponential backoff delay
  const consecutiveErrors = useRef<number>(0) // Track consecutive 429 errors

  // Optimized message loading with caching
  const loadMessages = useCallback(async (forceRefresh = false) => {
    const now = Date.now()
    const cacheKey = `messages_${applicationId}`
    
    // Check cache first (unless force refresh)
    if (!forceRefresh && messageCache.has(cacheKey)) {
      const cached = messageCache.get(cacheKey)!
      const timeSinceCache = now - lastLoadTime.current
      
      if (timeSinceCache < CACHE_DURATION) {
        console.log('Using cached messages, loaded', cached.length, 'messages')
        setMessages(cached)
        // Refresh the cache timestamp so we don't immediately refetch again
        lastLoadTime.current = now
        return
      }
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        console.error('No auth token found')
        setLoading(false)
        return
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/messages/application/${applicationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        // Handle rate limiting (429) with exponential backoff
        if (response.status === 429) {
          consecutiveErrors.current += 1
          // Exponential backoff: 10s, 20s, 40s, max 60s
          rateLimitBackoff.current = Math.min(10000 * Math.pow(2, consecutiveErrors.current - 1), 60000)
          console.warn(`Rate limited. Backing off for ${rateLimitBackoff.current / 1000}s`)
          throw new Error(`Rate limited: Too many requests. Please wait ${Math.ceil(rateLimitBackoff.current / 1000)} seconds.`)
        }
        // Reset error counter on non-rate-limit errors
        consecutiveErrors.current = 0
        rateLimitBackoff.current = 0
        throw new Error(`Failed to load messages: ${response.statusText}`)
      }

      // Reset error counters on success
      consecutiveErrors.current = 0
      rateLimitBackoff.current = 0

      const data = await response.json()
      const messagesArray = (data.messages || []) as BackendMessage[]

      // Helper to normalize ID (handle both _id and id formats)
      const normalizeId = (id: NormalizableId): string => {
        if (!id) return ''
        if (typeof id === 'string') return id
        if (typeof id === 'object' && id._id) return String(id._id)
        if (typeof id === 'object' && id.id) return String(id.id)
        return String(id)
      }

      // Helper to normalize role from backend to frontend format
      const normalizeRole = (role: string | undefined): UserRole => {
        if (!role) return 'client'
        const normalized = role.toLowerCase()
        if (normalized === 'landlord' || normalized === 'client' || normalized === 'admin') {
          return normalized as UserRole
        }
        return 'client' // default fallback
      }

      // Transform backend messages to frontend format
      const transformedMessages: MessageWithUser[] = messagesArray.map((msg: BackendMessage) => {
        // Extract sender and receiver objects
        const senderObj = typeof msg.sender === 'object' && msg.sender !== null ? msg.sender : null
        const receiverObj = typeof msg.receiver === 'object' && msg.receiver !== null ? msg.receiver : null
        
        // Normalize sender_id - check all possible locations
        const senderId = normalizeId(
          senderObj ? (senderObj._id || senderObj.id) : (msg.sender_id || msg.sender)
        ) || ''
        const receiverId = normalizeId(
          receiverObj ? (receiverObj._id || receiverObj.id) : (msg.receiver_id || msg.receiver)
        ) || ''
        
        // Normalize application_id
        const applicationIdValue = typeof msg.application === 'object' && msg.application !== null
          ? (msg.application._id || msg.application.id || '')
          : (typeof msg.application === 'string' ? msg.application : msg.applicationId || '')
        
        return {
          id: msg._id || msg.id || '',
          application_id: applicationIdValue,
          sender_id: senderId,
          receiver_id: receiverId,
          content: msg.message || msg.content || '',
          messageType: msg.messageType || msg.message_type || 'text',
          attachments: msg.attachments || [],
          isRead: msg.isRead ?? msg.is_read,
          created_at: msg.sentAt || msg.sent_at || msg.createdAt || msg.created_at || new Date().toISOString(),
          updated_at: msg.updatedAt || msg.updated_at || new Date().toISOString(),
          sender: {
            id: senderId,
            email: senderObj?.email || '',
            role: normalizeRole(senderObj?.role),
            is_verified: senderObj?.isVerified || senderObj?.is_verified || false,
            created_at: senderObj?.createdAt || senderObj?.created_at || new Date().toISOString(),
            firstName: senderObj?.firstName,
            lastName: senderObj?.lastName,
            ...(senderObj?.avatar ? { avatar: senderObj.avatar } : {})
          },
          receiver: {
            id: receiverId,
            email: receiverObj?.email || '',
            role: normalizeRole(receiverObj?.role),
            is_verified: receiverObj?.isVerified || receiverObj?.is_verified || false,
            created_at: receiverObj?.createdAt || receiverObj?.created_at || new Date().toISOString(),
            firstName: receiverObj?.firstName,
            lastName: receiverObj?.lastName,
            ...(receiverObj?.avatar ? { avatar: receiverObj.avatar } : {})
          }
        }
      })

      // Merge with existing messages to preserve optimistic updates
      setMessages(prev => {
        // Create a map of existing messages by ID for quick lookup
        const existingMap = new Map(prev.map(m => [m.id, m]))
        
        // Add/update messages from server
        transformedMessages.forEach(msg => {
          existingMap.set(msg.id, msg)
        })
        
        // Convert back to array and sort by created_at
        const merged = Array.from(existingMap.values())
        merged.sort((a, b) => {
          const timeA = new Date(a.created_at).getTime()
          const timeB = new Date(b.created_at).getTime()
          return timeA - timeB
        })
        
        return merged
      })
      
      // Update cache with merged messages
      const cacheKey = `messages_${applicationId}`
      const currentMessages = Array.from(new Map(
        [...(messageCache.get(cacheKey) || []), ...transformedMessages].map(m => [m.id, m])
      ).values())
      currentMessages.sort((a, b) => {
        const timeA = new Date(a.created_at).getTime()
        const timeB = new Date(b.created_at).getTime()
        return timeA - timeB
      })
      messageCache.set(cacheKey, currentMessages)
      lastLoadTime.current = now

      // Best-effort: mark conversation as read for the current user (backend ignores if no access).
      const apiUrlForRead = getApiUrl()
      fetch(`${apiUrlForRead}/messages/application/${applicationId}/read-all`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      }).catch(() => {
        // non-blocking
      })
      
    } catch (error) {
      console.error('Error loading messages:', error)
    } finally {
      setLoading(false)
    }
  }, [applicationId])

  // Send message
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || sending) return

    setSending(true)
    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        throw new Error('No auth token found')
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          applicationId: applicationId,
          message: content.trim(),
          messageType: 'text',
          attachments: []
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Failed to send message: ${response.statusText}`)
      }

      const data = await response.json()
      const msg = (data.data || data) as BackendMessage

      // Helper to normalize ID
      const normalizeId = (id: NormalizableId): string => {
        if (!id) return ''
        if (typeof id === 'string') return id
        if (typeof id === 'object' && id._id) return String(id._id)
        if (typeof id === 'object' && id.id) return String(id.id)
        return String(id)
      }

      // Normalize sender and receiver IDs
      const senderId = normalizeId(
        (typeof msg.sender === 'object' && msg.sender !== null 
          ? (msg.sender._id || msg.sender.id) 
          : null) || msg.sender_id || msg.sender
      ) || currentUserId
      const receiverId = normalizeId(
        (typeof msg.receiver === 'object' && msg.receiver !== null
          ? (msg.receiver._id || msg.receiver.id)
          : null) || msg.receiver_id || msg.receiver
      )

      // Helper to normalize role from backend to frontend format
      const normalizeRole = (role: string | undefined): UserRole => {
        if (!role) return 'client'
        const normalized = role.toLowerCase()
        if (normalized === 'landlord' || normalized === 'client' || normalized === 'admin') {
          return normalized as UserRole
        }
        return 'client' // default fallback
      }

      // Transform backend message to frontend format
      const sender = typeof msg.sender === 'object' && msg.sender !== null ? msg.sender : null
      const receiver = typeof msg.receiver === 'object' && msg.receiver !== null ? msg.receiver : null

      // Ensure we have a valid message ID - generate temporary ID if backend doesn't provide one
      const messageId = msg._id || msg.id || `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      
      const newMessage: MessageWithUser = {
        id: messageId,
        application_id: typeof msg.application === 'object' && msg.application !== null
          ? (msg.application._id || msg.application.id || '')
          : (typeof msg.application === 'string' ? msg.application : applicationId),
        sender_id: senderId,
        receiver_id: receiverId,
        content: msg.message || msg.content || content.trim(),
        created_at: msg.sentAt || msg.sent_at || msg.createdAt || msg.created_at || new Date().toISOString(),
        updated_at: msg.updatedAt || msg.updated_at || new Date().toISOString(),
        sender: {
          id: senderId,
          email: sender?.email || '',
          role: normalizeRole(sender?.role),
          is_verified: sender?.isVerified || sender?.is_verified || false,
          created_at: sender?.createdAt || sender?.created_at || new Date().toISOString(),
          firstName: sender?.firstName,
          lastName: sender?.lastName,
          ...(sender?.avatar ? { avatar: sender.avatar } : {})
        },
        receiver: {
          id: receiverId,
          email: receiver?.email || '',
          role: normalizeRole(receiver?.role),
          is_verified: receiver?.isVerified || receiver?.is_verified || false,
          created_at: receiver?.createdAt || receiver?.created_at || new Date().toISOString(),
          firstName: receiver?.firstName,
          lastName: receiver?.lastName,
          ...(receiver?.avatar ? { avatar: receiver.avatar } : {})
        }
      }

      // Add message optimistically to state and keep chronological order (oldest first)
      setMessages(prev => {
        const exists = prev.some(m => m.id === newMessage.id)
        if (exists) return prev
        const merged = [...prev, newMessage]
        merged.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
        return merged
      })
      
      // Update cache
      const cacheKey = `messages_${applicationId}`
      const cached = messageCache.get(cacheKey) || []
      // Check for duplicates in cache too
      const cacheExists = cached.some(m => m.id === newMessage.id)
      if (!cacheExists) {
        messageCache.set(cacheKey, [...cached, newMessage])
      }
      
      // Reload messages after a longer delay to ensure backend has processed it
      // Use a longer delay (2 seconds) to give the backend time to index the message
      setTimeout(() => {
        loadMessages(true).then(() => {
          // After reload, ensure our sent message is still in the list
          // This handles cases where the backend response might be slightly delayed
          setMessages(prev => {
            const hasNewMessage = prev.some(m => m.id === newMessage.id)
            if (!hasNewMessage) {
              // If the reloaded messages don't include our new message yet, add it back
              return [...prev, newMessage]
            }
            return prev
          })
        }).catch(() => {
          // If reload fails, keep the optimistic message
          console.warn('Failed to reload messages after send, keeping optimistic message')
        })
      }, 2000)
      
    } catch (error) {
      console.error('Error sending message:', error)
    } finally {
      setSending(false)
    }
  }, [applicationId, currentUserId, sending, loadMessages])

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  // Load messages on mount
  useEffect(() => {
    if (applicationId && currentUserId) {
      loadMessages()
    }
  }, [applicationId, currentUserId, loadMessages])

  // Real-time polling for new messages with rate limit handling
  useEffect(() => {
    if (!applicationId || !currentUserId) return

    let pollInterval: NodeJS.Timeout | null = null
    let backoffTimeout: NodeJS.Timeout | null = null

    const scheduleNextPoll = () => {
      // Clear any existing interval/timeout
      if (pollInterval) clearInterval(pollInterval)
      if (backoffTimeout) clearTimeout(backoffTimeout)

      // If we're in backoff, wait before resuming polling
      if (rateLimitBackoff.current > 0) {
        backoffTimeout = setTimeout(() => {
          rateLimitBackoff.current = 0 // Reset backoff after waiting
          consecutiveErrors.current = 0 // Reset error count
          scheduleNextPoll() // Resume normal polling
        }, rateLimitBackoff.current)
      } else {
        // Normal polling
        pollInterval = setInterval(() => {
          loadMessages(true).catch(() => {
            // Error already logged in loadMessages
            // If it's a rate limit error, the backoff will be set
            // and scheduleNextPoll will handle it
          })
        }, POLLING_INTERVAL)
      }
    }

    // Start polling
    scheduleNextPoll()

    // Cleanup on unmount
    return () => {
      if (pollInterval) clearInterval(pollInterval)
      if (backoffTimeout) clearTimeout(backoffTimeout)
    }
  }, [applicationId, currentUserId, loadMessages])

  // Cleanup cache when component unmounts
  useEffect(() => {
    return () => {
      const cacheKey = `messages_${applicationId}`
      messageCache.delete(cacheKey)
    }
  }, [applicationId])

  return {
    messages,
    loading,
    sending,
    sendMessage,
    loadMessages,
    messagesEndRef
  }
}
